import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiPlus, FiRepeat, FiShuffle, FiSliders, FiTrash2, FiX, FiMusic } from 'react-icons/fi';
import Dropdown from '../Dropdown/Dropdown';
import '../Dropdown/Dropdown.css';
import { deletePlaylist, listPlaylists, savePlaylist } from './audioPlaylistStorage';
import { isFlatEqGains } from './useWebAudioEqualizer';
import { subscribeAudioLibraryChanged } from './audioLibraryEvents';
import ExpandableText from './ExpandableText';

const SAVED_PLAYLIST_PLACEHOLDER = 'Load saved playlist…';

export default function AudioPlayerPlaylistPanel({
  playlistName,
  onPlaylistNameChange,
  tracks,
  currentTrackId,
  shuffle,
  onShuffleChange,
  repeat = false,
  onRepeatChange,
  onAddCurrentTrack,
  addCurrentLabel = 'Add current track',
  onSelectTrack,
  onRemoveTrack,
  onLoadTracks,
  browseSlot = null,
  statusMessage = '',
  onStatusMessage,
  className = '',
  linkedSavedPlaylistId = '',
  eqGains = null,
  onOpenEqualizer = null,
  equalizerOpen = false,
}) {
  const [savedPlaylists, setSavedPlaylists] = useState([]);
  const [saveBusy, setSaveBusy] = useState(false);
  const [selectedSavedId, setSelectedSavedId] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const autoSaveSkipRef = useRef(true);

  const refreshSaved = useCallback(async () => {
    try {
      const rows = await listPlaylists();
      setSavedPlaylists(rows);
    } catch {
      setSavedPlaylists([]);
    }
  }, []);

  useEffect(() => {
    refreshSaved();
    return subscribeAudioLibraryChanged(() => {
      refreshSaved();
    });
  }, [refreshSaved]);

  useEffect(() => {
    if (!linkedSavedPlaylistId) return;
    autoSaveSkipRef.current = true;
    setSelectedSavedId(String(linkedSavedPlaylistId));
  }, [linkedSavedPlaylistId]);

  const savedPlaylistOptions = useMemo(
    () =>
      savedPlaylists.map((p) => ({
        value: p.id,
        label: `${p.name} (${p.tracks?.length || 0})`,
      })),
    [savedPlaylists]
  );

  const getSavedPlaylistLabel = useCallback(
    (value) => {
      if (!value) return SAVED_PLAYLIST_PLACEHOLDER;
      const match = savedPlaylistOptions.find((o) => o.value === value);
      return match?.label || SAVED_PLAYLIST_PLACEHOLDER;
    },
    [savedPlaylistOptions]
  );

  const activeSavedId = selectedSavedId || linkedSavedPlaylistId || '';

  const persistPlaylist = useCallback(
    async ({ name, id, silent = false }) => {
      if (!tracks.length) {
        onStatusMessage?.('Add at least one track before saving.');
        return null;
      }
      const playlistId = id || activeSavedId || undefined;
      if (silent && !playlistId) return null;
      setSaveBusy(true);
      try {
        const saved = await savePlaylist({
          id: playlistId,
          name: String(name || playlistName || 'My playlist').trim() || 'My playlist',
          tracks,
          shuffle,
          repeat,
          eqGains,
        });
        setSelectedSavedId(saved.id);
        await refreshSaved();
        if (!silent) onStatusMessage?.('Playlist saved.');
        return saved;
      } catch (e) {
        onStatusMessage?.(e.message || 'Could not save playlist.');
        return null;
      } finally {
        setSaveBusy(false);
      }
    },
    [
      tracks,
      shuffle,
      repeat,
      eqGains,
      playlistName,
      activeSavedId,
      refreshSaved,
      onStatusMessage,
    ]
  );

  useEffect(() => {
    if (!activeSavedId || !tracks.length) return undefined;
    if (autoSaveSkipRef.current) {
      autoSaveSkipRef.current = false;
      return undefined;
    }
    const timer = window.setTimeout(() => {
      persistPlaylist({ id: activeSavedId, name: playlistName, silent: true }).catch(() => {});
    }, 700);
    return () => window.clearTimeout(timer);
  }, [tracks, shuffle, repeat, eqGains, activeSavedId, playlistName, persistPlaylist]);

  const startEditName = () => {
    setDraftName(playlistName || '');
    setEditingName(true);
  };

  const cancelEditName = () => {
    setEditingName(false);
    setDraftName('');
  };

  const confirmEditName = async () => {
    const name = draftName.trim() || 'My playlist';
    onPlaylistNameChange?.(name);
    setEditingName(false);
    setDraftName('');
    await persistPlaylist({ name });
  };

  const handleLoadSaved = (id) => {
    autoSaveSkipRef.current = true;
    setSelectedSavedId(id);
    if (!id) return;
    const row = savedPlaylists.find((p) => p.id === id);
    if (!row) return;
    onPlaylistNameChange?.(row.name);
    onShuffleChange?.(Boolean(row.shuffle));
    onRepeatChange?.(Boolean(row.repeat));
    onLoadTracks?.(row.tracks || [], {
      id: row.id,
      name: row.name,
      shuffle: row.shuffle,
      repeat: row.repeat,
      eqGains: row.eqGains,
    });
    onStatusMessage?.(`Loaded “${row.name}”.`);
  };

  const handleDeleteSavedPlaylist = async () => {
    if (!selectedSavedId) {
      onStatusMessage?.('Load or save a playlist first.');
      return;
    }
    const row = savedPlaylists.find((p) => p.id === selectedSavedId);
    const label = row?.name || 'Playlist';
    try {
      await deletePlaylist(selectedSavedId);
      setSelectedSavedId('');
      autoSaveSkipRef.current = true;
      await refreshSaved();
      onStatusMessage?.(`Deleted saved playlist “${label}”. Queue tracks are unchanged.`);
    } catch (e) {
      onStatusMessage?.(e.message || 'Could not delete playlist.');
    }
  };

  return (
    <aside className={`audio-playlist-panel ${className}`.trim()} aria-label="Playlist">
      <div className="audio-playlist-panel__head">
        <h3 className="audio-playlist-panel__title">Playlist</h3>
        <div className="audio-playlist-panel__head-options">
          <label className="audio-playlist-panel__shuffle">
            <input
              type="checkbox"
              checked={shuffle}
              onChange={(e) => onShuffleChange?.(e.target.checked)}
            />
            <FiShuffle aria-hidden />
            <span>Shuffle</span>
          </label>
          <label className="audio-playlist-panel__shuffle">
            <input
              type="checkbox"
              checked={repeat}
              onChange={(e) => onRepeatChange?.(e.target.checked)}
            />
            <FiRepeat aria-hidden />
            <span>Repeat</span>
          </label>
          {onOpenEqualizer ? (
            <button
              type="button"
              className={`audio-playlist-panel__eq-btn${
                equalizerOpen || !isFlatEqGains(eqGains) ? ' audio-playlist-panel__eq-btn--active' : ''
              }`}
              aria-label={equalizerOpen ? 'Hide equalizer' : 'Show equalizer'}
              aria-pressed={Boolean(equalizerOpen)}
              title="Equalizer"
              onClick={() => onOpenEqualizer()}
            >
              <FiSliders aria-hidden />
              <span>EQ</span>
            </button>
          ) : null}
        </div>
      </div>

      <div className="audio-playlist-panel__name-row">
        <div className="audio-playlist-panel__name-block">
          {editingName ? (
            <div className="audio-playlist-panel__name-edit">
              <input
                type="text"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder="My playlist"
                maxLength={120}
                aria-label="Playlist name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    confirmEditName();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEditName();
                  }
                }}
              />
              <div className="audio-playlist-panel__name-edit-actions">
                <button
                  type="button"
                  className="audio-playlist-panel__name-action audio-playlist-panel__name-action--confirm"
                  aria-label="Save playlist name"
                  disabled={saveBusy}
                  onClick={confirmEditName}
                >
                  <FiCheck aria-hidden />
                </button>
                <button
                  type="button"
                  className="audio-playlist-panel__name-action audio-playlist-panel__name-action--cancel"
                  aria-label="Cancel rename"
                  disabled={saveBusy}
                  onClick={cancelEditName}
                >
                  <FiX aria-hidden />
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="audio-playlist-panel__name-text"
              onClick={startEditName}
              title="Click to rename"
            >
              {playlistName || 'My playlist'}
            </button>
          )}
        </div>
        {selectedSavedId && !editingName ? (
          <button
            type="button"
            className="audio-playlist-panel__icon-btn audio-playlist-panel__delete-playlist-icon"
            aria-label="Delete saved playlist"
            title="Delete saved playlist (queue tracks stay)"
            onClick={handleDeleteSavedPlaylist}
          >
            <FiTrash2 aria-hidden />
          </button>
        ) : null}
      </div>

      <div className="audio-playlist-panel__actions">
        <button type="button" className="audio-playlist-panel__btn" onClick={onAddCurrentTrack}>
          <FiPlus aria-hidden /> {addCurrentLabel}
        </button>
      </div>

      <div className="audio-playlist-panel__saved">
        <div className="audio-playlist-panel__saved-dropdown" aria-label="Load saved playlist">
          <Dropdown
            options={savedPlaylistOptions}
            selectedOption={selectedSavedId || ''}
            onSelect={(opt) => handleLoadSaved(opt?.value)}
            getSelectedLabel={getSavedPlaylistLabel}
            searchable={savedPlaylistOptions.length > 6}
            searchPlaceholder="Search saved playlists…"
            menuPortal
            menuPortalMinWidth={260}
            menuPortalClassName="reusable-dropdown-menu--audio-playlist-saved"
          />
        </div>
      </div>

      {browseSlot ? <div className="audio-playlist-panel__browse">{browseSlot}</div> : null}

      <ol className="audio-playlist-panel__tracks">
        {tracks.length === 0 ? (
          <li className="audio-playlist-panel__empty">No tracks yet. Add songs to build your playlist.</li>
        ) : (
          tracks.map((track, index) => {
            const active = track.trackId === currentTrackId;
            return (
              <li key={track.trackId}>
                <button
                  type="button"
                  className={`audio-playlist-panel__track${active ? ' audio-playlist-panel__track--active' : ''}`}
                  onClick={() => onSelectTrack?.(track.trackId)}
                >
                  <FiMusic aria-hidden />
                  <span className="audio-playlist-panel__track-index">{index + 1}</span>
                  <span className="audio-playlist-panel__track-title-wrap">
                    <ExpandableText
                      text={track.title}
                      className="audio-playlist-panel__track-title"
                      maxLines={2}
                      stopClickPropagation
                      toggleStacked
                    />
                    {track.streamMetaLine ? (
                      <ExpandableText
                        text={track.streamMetaLine}
                        className="audio-playlist-panel__track-meta"
                        maxLines={2}
                        stopClickPropagation
                        toggleStacked
                      />
                    ) : null}
                  </span>
                </button>
                <button
                  type="button"
                  className="audio-playlist-panel__icon-btn"
                  aria-label={`Remove ${track.title} from queue`}
                  onClick={() => onRemoveTrack?.(track.trackId)}
                >
                  <FiTrash2 aria-hidden />
                </button>
              </li>
            );
          })
        )}
      </ol>

      {statusMessage ? (
        <p className="audio-playlist-panel__status" role="status">
          {statusMessage}
        </p>
      ) : null}
    </aside>
  );
};
